// Company management utilities
import { COMPANIES, Company, Branch } from './companyData'
import {
  supabaseClient,
  isSupabaseConfigured,
  generateSecureId,
} from '../lib/supabase'

export interface Employee {
  id: string
  name: string
  email: string
  username?: string
  // password intentionally excluded from frontend model
  position: string
  companyId: string | number
  branchId: string
  status: string
  permissions: any
}

// Convert Supabase employee to app format (NO password)
function convertSupabaseEmployee(supabaseEmployee: any): Employee {
  return {
    id: supabaseEmployee.id,
    name: supabaseEmployee.name,
    email: supabaseEmployee.email,
    username: supabaseEmployee.username,
    position: supabaseEmployee.position,
    companyId: supabaseEmployee.company_id,
    branchId: supabaseEmployee.branch_id,
    status: supabaseEmployee.status,
    permissions: supabaseEmployee.permissions,
  }
}

// Convert app employee to Supabase format
function convertToSupabaseEmployee(employee: any): any {
  const result: any = {}
  if (employee.id !== undefined) result.id = employee.id
  if (employee.name !== undefined) result.name = employee.name
  if (employee.email !== undefined) result.email = employee.email
  if (employee.username !== undefined) result.username = employee.username
  // Only include password if explicitly provided (for create/update)
  if (employee.password) result.password = employee.password
  if (employee.position !== undefined) result.position = employee.position
  if (employee.companyId !== undefined) result.company_id = employee.companyId
  if (employee.branchId !== undefined) result.branch_id = employee.branchId
  if (employee.status !== undefined) result.status = employee.status
  if (employee.permissions !== undefined)
    result.permissions = employee.permissions
  return result
}

// Get employees from Supabase (without passwords)
export async function getEmployees(): Promise<Employee[]> {
  if (!isSupabaseConfigured()) {
    throw new Error(
      'Supabase no está configurado. Por favor configura las variables de entorno.',
    )
  }

  try {
    const supabaseEmployees = await supabaseClient.getEmployees()
    return supabaseEmployees.map(convertSupabaseEmployee)
  } catch (error) {
    throw error
  }
}

// Add new employee
export async function addEmployee(employee: any): Promise<void> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase no está configurado')
  }

  try {
    const supabaseEmployee = convertToSupabaseEmployee(employee)
    await supabaseClient.createEmployee(supabaseEmployee)
  } catch (error) {
    throw error
  }
}

// Update employee
export async function updateEmployee(
  id: string,
  updates: Partial<Employee> & { password?: string },
): Promise<void> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase no está configurado')
  }

  try {
    const supabaseUpdates = convertToSupabaseEmployee(updates)
    await supabaseClient.updateEmployee(id, supabaseUpdates)
  } catch (error) {
    throw error
  }
}

// Delete employee
export async function deleteEmployee(id: string): Promise<void> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase no está configurado')
  }

  try {
    await supabaseClient.deleteEmployee(id)
  } catch (error) {
    throw error
  }
}

// Get employee count by company
export async function getEmployeeCountByCompany(
  companyId: string | number,
): Promise<number> {
  const employees = await getEmployees()
  return employees.filter((e) => String(e.companyId) === String(companyId))
    .length
}

// Get companies from localStorage (with branches)
export function getCompanies(): Company[] {
  const saved = localStorage.getItem('criket_companies')
  return saved ? JSON.parse(saved) : []
}

// Save companies to localStorage
export function saveCompanies(companies: Company[]): void {
  localStorage.setItem('criket_companies', JSON.stringify(companies))
}

// Get branches by company ID
export function getBranchesByCompany(companyId: string | number): Branch[] {
  const companies = getCompanies()
  const company = companies.find((c) => String(c.id) === String(companyId))
  return company?.branches || []
}

// Add new company
export function addCompany(company: Omit<Company, 'id'>): Company {
  const companies = getCompanies()
  const newId = Math.max(...companies.map((c) => c.id), 0) + 1
  const newCompany: Company = { ...company, id: newId }
  saveCompanies([...companies, newCompany])
  return newCompany
}

// Update company
export function updateCompany(
  id: string | number,
  updates: Partial<Company>,
): void {
  const companies = getCompanies()
  const updated = companies.map((c) =>
    String(c.id) === String(id) ? { ...c, ...updates } : c,
  )
  saveCompanies(updated)
}

// Add branch to company
export function addBranchToCompany(
  companyId: string | number,
  branch: Omit<Branch, 'id' | 'companyId'>,
): void {
  const companies = getCompanies()
  const company = companies.find((c) => String(c.id) === String(companyId))
  if (!company) return

  const newBranch: Branch = {
    ...branch,
    id: generateSecureId('branch'),
    companyId,
  }

  company.branches.push(newBranch)
  saveCompanies(companies)
}

// Remove branch from company
export function removeBranchFromCompany(
  companyId: string | number,
  branchId: string,
): void {
  const companies = getCompanies()
  const company = companies.find((c) => String(c.id) === String(companyId))
  if (!company) return

  company.branches = company.branches.filter((b) => b.id !== branchId)
  saveCompanies(companies)
}
